import { XCircleIcon } from '@heroicons/react/24/solid';
import { Suspense } from 'react';

import { AppRouteBody } from '@/components/AppRouteBody';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Spinner } from '@/components/ui/spinner';
import {
  SettingContentCard,
  SettingSectionHeader,
} from '@/sections/settings/components';
import { UserProfileCards } from '@/sections/settings/components/cards/UserProfileCards';
import { useAuth } from '@/state/auth';

/**
 * Profile page - standalone page for user profile settings.
 *
 * CRITICAL: This page IGNORES impersonation.
 * It ALWAYS shows the logged-in user's own profile settings.
 *
 * Accessible from:
 * - AccountDropdown "User Profile" link
 * - Direct URL navigation to /profile
 *
 * NOT accessible from:
 * - Settings page (Settings has "User Management" tab for user tables only)
 *
 * Rationale:
 * - Profile settings are always "MY settings" - no ambiguity
 * - Clear separation: Profile is personal settings, User Management is administration
 * - AccountDropdown "User Profile" link should always show own profile
 * - Security: Prevents accidental modification of other users' profiles
 *
 * Features:
 * - Profile picture upload and display name editing
 * - Interface theme selection (system/dark/light)
 * - Authentication method display
 * - Password change (local auth only)
 * - MFA enrollment (local auth only)
 * - API key generation and management
 *
 * @see {@link UserProfileCards} Core profile settings implementation
 */
export function Profile() {
  const { ssoDomain, isPraetorianUser } = useAuth();

  // NOTE: We intentionally do NOT use isImpersonating or friend here
  // Profile always shows logged-in user's own settings

  return (
    <AppRouteBody title="User Profile">
      <div className="flex h-full flex-col gap-14 overflow-auto pr-4">
        <div className="flex flex-col gap-6">
          <SettingSectionHeader
            title="My Profile"
            description={
              ssoDomain
                ? 'Manage your profile, API keys, and interface preferences. Your organization uses SSO for authentication.'
                : !isPraetorianUser
                  ? 'Manage your profile, password, two-factor authentication, API keys, and interface preferences.'
                  : 'Manage your profile, API keys, and interface preferences.'
            }
          />

          <SettingContentCard data-testid="user-profile-page-card">
            <ErrorBoundary
              fallback={
                <div className="flex h-full flex-col items-center justify-center gap-4 py-12">
                  <XCircleIcon className="text-chariot-status-critical size-12" />
                  <div className="flex flex-col gap-2 text-center">
                    <h2 className="text-chariot-text-primary text-lg font-bold">
                      Failed to Load Profile Settings
                    </h2>
                    <p className="text-chariot-text-secondary max-w-md text-sm">
                      Unable to retrieve profile settings. Please refresh the
                      page or contact support.
                    </p>
                  </div>
                </div>
              }
            >
              <Suspense
                fallback={
                  <div className="flex h-full flex-col items-center justify-center gap-4 py-12">
                    <Spinner size="xl" />
                    <p className="text-chariot-text-secondary text-sm">
                      Loading profile settings...
                    </p>
                  </div>
                }
              >
                {/* No email prop - UserProfileCards always uses logged-in user (me) */}
                <UserProfileCards />
              </Suspense>
            </ErrorBoundary>
          </SettingContentCard>
        </div>
      </div>
    </AppRouteBody>
  );
}
